import type { CoreError } from '@ingenico/api-sdk-core/common';
import type { DeviceResourceMetricsSnapshotResourceApi } from '@ingenico/api-sdk-device-management-private';
import { useBusinessService } from '@ingenico/react-section-utils';
import { requestAndUpdateState } from '@ingenico/react-utils/functions';
import type { UseAsyncDataState } from '@ingenico/react-utils/hooks';
import { useAsyncDataState, useSafeLeakSubscriber, useSubject } from '@ingenico/react-utils/hooks';
import { useUnmountEffect, useUpdateEffect } from '@react-hookz/web';
import { isFunction } from 'lodash-es';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { Observable } from 'rxjs';
import { startWith, switchMap, takeUntil, tap, timer } from 'rxjs';
import { map } from 'rxjs/operators';
import { DeviceManagementService } from '../domain/business/services/deviceManagement.service';
import { DeviceConnectionStatusType } from '../domain/models';
import type { AskDeviceUpdateRequest } from '../domain/models/askDeviceUpdate.model';
import { AskDeviceUpdateType } from '../domain/models/askDeviceUpdate.model';
import { useDeviceManagementCapability } from './useDeviceManagementCapability.hook';

export interface UseDevicePropertiesRequestTimerReturn {
  readonly handleRefreshDeviceProperties: () => void;
  readonly askDeviceUpdateRequestState: UseAsyncDataState<unknown, CoreError>;
}

export interface UseDevicePropertiesRequestTimerParams<T> {
  readonly logicalDeviceId: T;
  readonly onAskRefetchError?: () => void;
  readonly onAskRefetchSuccess?: () => void;
  readonly onAskRefetchInit?: () => void;
  readonly customRequest?: () => void;
  readonly hasError?: boolean;
  readonly enable?: boolean;
  readonly askMultipleDeviceUpdate?: (
    requestData: AskDeviceUpdateRequest[],
  ) => Observable<DeviceResourceMetricsSnapshotResourceApi[]>;
  readonly askDeviceUpdate?: (
    requestData: AskDeviceUpdateRequest,
  ) => Observable<DeviceResourceMetricsSnapshotResourceApi>;
  readonly deviceConnectionStatus?: DeviceConnectionStatusType;
}

export const GET_LOGICAL_DEVICE_DATA_INTERVAL = 10_000;

export function useDevicePropertiesRequestTimer<T extends string[] | string = string>({
  logicalDeviceId,
  onAskRefetchError: onError,
  onAskRefetchSuccess: onSuccess,
  onAskRefetchInit: onInit,
  customRequest,
  hasError,
  enable = true,
  deviceConnectionStatus,
}: UseDevicePropertiesRequestTimerParams<T>): UseDevicePropertiesRequestTimerReturn {
  const hasInitializedMetricsData = useRef<boolean>(false);
  const isSubscribed = useRef<boolean>(false);
  const makeRequestRef = useRef<() => void>();
  const safeLeaskSubscriber = useSafeLeakSubscriber();
  const triggerRequestCall = useSubject();
  const cleanUpTriggerRequestCall = useSubject();

  const deviceManagementService = useBusinessService(DeviceManagementService);
  const [askDeviceUpdateRequestState, setAskDeviceUpdateRequestState] = useAsyncDataState<unknown, CoreError>();

  const hasDeviceStateReadPermission = useDeviceManagementCapability('logicalDevice.deviceStateRead');

  const shouldSkipDeviceOperations = useMemo(() => !hasDeviceStateReadPermission, [hasDeviceStateReadPermission]);

  const getRequestBody = useCallback(
    (deviceId: string) => ({
      deviceId,
      resource: [AskDeviceUpdateType.BasicRefreshLocation, AskDeviceUpdateType.Hardware, AskDeviceUpdateType.General],
    }),
    [],
  );

  const getRequest = useCallback(
    (logicalDeviceData: string[] | string) => {
      if (isFunction(customRequest)) {
        return customRequest();
      }

      if (deviceConnectionStatus !== DeviceConnectionStatusType.Connected) {
        return null;
      }

      const requestData = Array.isArray(logicalDeviceData)
        ? logicalDeviceData.map((deviceId) => getRequestBody(deviceId))
        : getRequestBody(logicalDeviceData);
      return Array.isArray(logicalDeviceData)
        ? deviceManagementService.askMultipleDeviceUpdate?.(requestData as AskDeviceUpdateRequest[])
        : deviceManagementService.askDeviceUpdate?.(requestData as AskDeviceUpdateRequest);
    },
    [deviceConnectionStatus, customRequest, getRequestBody, deviceManagementService],
  );

  const makeRequest = useCallback(() => {
    if (shouldSkipDeviceOperations) {
      if (!hasInitializedMetricsData.current) {
        hasInitializedMetricsData.current = true;
        onInit?.();
      }
      return;
    }

    const request = getRequest(logicalDeviceId);
    if (request) {
      requestAndUpdateState(request, setAskDeviceUpdateRequestState, {
        onSuccess,
        onError,
      });
    } else if (!hasInitializedMetricsData.current) {
      hasInitializedMetricsData.current = true;
      onInit?.();
    }
  }, [
    getRequest,
    logicalDeviceId,
    onError,
    onInit,
    onSuccess,
    setAskDeviceUpdateRequestState,
    shouldSkipDeviceOperations,
  ]);

  makeRequestRef.current = makeRequest;

  useEffect(() => {
    if (enable && !shouldSkipDeviceOperations) {
      cleanUpTriggerRequestCall.next(void 0);
      isSubscribed.current = false;
      if (!isSubscribed.current) {
        isSubscribed.current = true;
        safeLeaskSubscriber(
          triggerRequestCall.asObservable().pipe(
            startWith(void 0),
            switchMap(() =>
              timer(0, GET_LOGICAL_DEVICE_DATA_INTERVAL).pipe(
                tap(() => {
                  makeRequestRef.current?.();
                }),
                map(() => void 0),
              ),
            ),
            takeUntil(cleanUpTriggerRequestCall),
          ),
        );
      }
    }
  }, [
    deviceConnectionStatus,
    enable,
    cleanUpTriggerRequestCall,
    safeLeaskSubscriber,
    triggerRequestCall,
    shouldSkipDeviceOperations,
  ]);

  useUnmountEffect(() => {
    cleanUpTriggerRequestCall.next(void 0);
  });

  useUpdateEffect(() => {
    if (hasError) {
      cleanUpTriggerRequestCall.next(void 0);
    }
  }, [hasError]);

  const handleRefreshDeviceProperties = useCallback(() => triggerRequestCall.next(void 0), [triggerRequestCall]);

  return useMemo(
    () => ({ handleRefreshDeviceProperties, askDeviceUpdateRequestState }),
    [handleRefreshDeviceProperties, askDeviceUpdateRequestState],
  );
}
